import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TerminalLine } from "../components/terminal/TerminalLine";
import { TerminalChatStream } from "../components/ai/TerminalChatStream";
import type { SessionType } from "@shukan/shared";
import { fetchJson } from "../lib/api";

const PRESETS: { id: SessionType; label: string; tagline: string }[] = [
  { id: "morning", label: "morning check-in", tagline: "plan today from yesterday + calendar" },
  { id: "evening", label: "evening review", tagline: "close out today, pre-plan tomorrow" },
  { id: "weekly", label: "weekly audit", tagline: "mon–sun retrospective + 1 question" },
  { id: "ondemand", label: "ask anything", tagline: "open chat · uses 7-day context" },
];

interface ProviderListItem {
  id: string;
  name: string;
  isActive: boolean;
}

interface ConversationItem {
  id: string;
  sessionType: SessionType;
  date: string;
  createdAt: string;
  preview: string;
}

interface FullConversation {
  id: string;
  sessionType: SessionType;
  date: string;
  messages: Array<{ role: string; content: string; ts: number }>;
  createdAt: string;
}

interface MemoryItem {
  id: string;
  content: string;
  memory_type: "preference" | "insight" | "goal";
  created_at: string;
}

interface ReportItem {
  id: string;
  date: string;
  createdAt: string;
  report: string;
}

export function ChatPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<"chat" | "reports" | "memories">("chat");
  const [session, setSession] = useState<SessionType>("ondemand");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [historyMessages, setHistoryMessages] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Memory additions
  const [newMemoryText, setNewMemoryText] = useState("");
  const [newMemoryType, setNewMemoryType] = useState<"preference" | "insight" | "goal">("insight");

  // Selected report
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  // Check providers
  const { data: providers } = useQuery({
    queryKey: ["ai-providers"],
    queryFn: () => fetchJson<ProviderListItem[]>("/api/ai/providers"),
  });
  const hasActiveProvider = providers?.some((p) => p.isActive) ?? false;

  // Fetch conversations history
  const { data: conversations, refetch: refetchConversations } = useQuery({
    queryKey: ["ai-conversations"],
    queryFn: () => fetchJson<ConversationItem[]>("/api/ai/conversations"),
    enabled: hasActiveProvider,
  });

  // Delete conversation mutation
  const deleteConversation = useMutation({
    mutationFn: (id: string) =>
      fetchJson<any>(`/api/ai/conversations/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-conversations"] });
    },
  });

  // Memories Queries & Mutations
  const { data: memories, isLoading: isLoadingMemories } = useQuery({
    queryKey: ["ai-memories"],
    queryFn: () => fetchJson<MemoryItem[]>("/api/ai/memories"),
    enabled: hasActiveProvider && activeTab === "memories",
  });

  const saveMemory = useMutation({
    mutationFn: (input: { content: string; memoryType: string }) =>
      fetchJson<MemoryItem>("/api/ai/memories", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-memories"] });
      setNewMemoryText("");
    },
  });

  const deleteMemory = useMutation({
    mutationFn: (id: string) =>
      fetchJson<any>(`/api/ai/memories/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai-memories"] });
    },
  });

  // Reports Queries & Mutations
  const { data: reports, isLoading: isLoadingReports } = useQuery({
    queryKey: ["ai-reports"],
    queryFn: () => fetchJson<ReportItem[]>("/api/ai/reports"),
    enabled: hasActiveProvider && activeTab === "reports",
  });

  const generateReport = useMutation({
    mutationFn: () =>
      fetchJson<ReportItem>("/api/ai/reports/generate", { method: "POST" }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["ai-reports"] });
      setSelectedReportId(data.id);
    },
  });

  async function loadConversation(id: string) {
    try {
      const details = await fetchJson<FullConversation>(`/api/ai/conversations/${id}`);
      setSession(details.sessionType);
      setConversationId(details.id);
      setHistoryMessages(details.messages);
      if (window.innerWidth < 768) {
        setShowHistory(false);
      }
    } catch (err) {
      console.error("[shukan] failed to load conversation details:", err);
    }
  }

  function startNewChat(type: SessionType) {
    setSession(type);
    setConversationId(null);
    setHistoryMessages([]);
  }

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (confirm("delete this conversation?")) {
      await deleteConversation.mutateAsync(id);
      if (conversationId === id) {
        setConversationId(null);
        setHistoryMessages([]);
      }
    }
  };

  const handleAddMemorySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMemoryText.trim()) return;
    saveMemory.mutate({ content: newMemoryText, memoryType: newMemoryType });
  };

  // Find currently selected report details
  const currentSelectedReport = reports?.find((r) => r.id === selectedReportId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem", height: "100%", boxSizing: "border-box" }}>
      {/* Tab Navigation header */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--border)", paddingBottom: "0.5rem", gap: "1rem" }}>
        <button
          type="button"
          onClick={() => setActiveTab("chat")}
          style={{
            background: "none",
            border: "none",
            color: activeTab === "chat" ? "var(--accent)" : "var(--fg-dim)",
            fontSize: "0.85rem",
            fontWeight: "bold",
            cursor: "pointer",
            padding: "0.25rem 0.5rem",
            borderBottom: activeTab === "chat" ? "2px solid var(--accent)" : "none",
          }}
        >
          $ agi_chat_control
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("reports")}
          style={{
            background: "none",
            border: "none",
            color: activeTab === "reports" ? "var(--accent)" : "var(--fg-dim)",
            fontSize: "0.85rem",
            fontWeight: "bold",
            cursor: "pointer",
            padding: "0.25rem 0.5rem",
            borderBottom: activeTab === "reports" ? "2px solid var(--accent)" : "none",
          }}
        >
          $ autonomous_reports
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("memories")}
          style={{
            background: "none",
            border: "none",
            color: activeTab === "memories" ? "var(--accent)" : "var(--fg-dim)",
            fontSize: "0.85rem",
            fontWeight: "bold",
            cursor: "pointer",
            padding: "0.25rem 0.5rem",
            borderBottom: activeTab === "memories" ? "2px solid var(--accent)" : "none",
          }}
        >
          $ agent_memory_logs
        </button>
      </div>

      {providers && !hasActiveProvider ? (
        <div style={{ border: "1px solid var(--danger)", padding: "0.75rem", fontSize: "0.8rem" }}>
          <TerminalLine accent>! no active AI provider</TerminalLine>
          <span style={{ color: "var(--fg-dim)" }}>
            add a provider in <a href="/settings" style={{ color: "var(--accent)" }}>Settings → AI</a> to enable AGI agent features.
          </span>
        </div>
      ) : (
        <>
          {/* TAB 1: AGI CHAT CONTROL */}
          {activeTab === "chat" && (
            <div className="shukan-chat-layout">
              {showHistory && (
                <div className="shukan-chat-sidebar">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <TerminalLine prompt accent>
                      <strong>history</strong>
                    </TerminalLine>
                    <button
                      type="button"
                      className="shukan-btn"
                      style={{ fontSize: "0.7rem", padding: "0.15rem 0.3rem" }}
                      onClick={() => startNewChat("ondemand")}
                    >
                      + new chat
                    </button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                    {conversations && conversations.length > 0 ? (
                      conversations
                        .filter((c) => (c.sessionType as string) !== "report") // filter out report summaries
                        .map((c) => {
                          const isActive = conversationId === c.id;
                          return (
                            <div
                              key={c.id}
                              onClick={() => loadConversation(c.id)}
                              style={{
                                border: `1px solid ${isActive ? "var(--accent)" : "var(--border)"}`,
                                padding: "0.4rem",
                                cursor: "pointer",
                                fontSize: "0.75rem",
                                backgroundColor: isActive ? "var(--bg-elevated)" : "transparent",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                <span style={{ color: "var(--accent)", marginRight: "0.25rem" }}>
                                  [{c.sessionType}]
                                </span>
                                <span style={{ color: "var(--fg-dim)", marginRight: "0.25rem" }}>
                                  {c.date}
                                </span>
                                <span style={{ color: "var(--fg)" }}>{c.preview}</span>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => handleDelete(e, c.id)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  color: "var(--danger)",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  padding: "0 0.2rem",
                                }}
                              >
                                x
                              </button>
                            </div>
                          );
                        })
                    ) : (
                      <div style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>
                        $ no saved conversations.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="shukan-chat-main">
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <div className="shukan-chat-presets">
                    {PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={session === p.id && !conversationId ? "shukan-btn shukan-btn-primary" : "shukan-btn"}
                        onClick={() => startNewChat(p.id)}
                        title={p.tagline}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="shukan-btn"
                    onClick={() => setShowHistory(!showHistory)}
                    style={{ alignSelf: "flex-end" }}
                  >
                    {showHistory ? "[close history]" : "[show history]"}
                  </button>
                </div>

                {conversationId && (
                  <div style={{ fontSize: "0.7rem", color: "var(--fg-dim)", borderBottom: "1px dashed var(--border)", paddingBottom: "0.4rem" }}>
                    $ viewing saved session: <span style={{ color: "var(--accent)" }}>{conversationId}</span>
                  </div>
                )}

                <TerminalChatStream
                  session={session}
                  conversationId={conversationId}
                  onConversationIdChange={(id) => {
                    setConversationId(id);
                    refetchConversations();
                  }}
                  historyMessages={historyMessages}
                />
              </div>
            </div>
          )}

          {/* TAB 2: AUTONOMOUS REPORTS */}
          {activeTab === "reports" && (
            <div style={{ display: "grid", gridTemplateColumns: "250px 1fr", gap: "1rem", height: "100%", minHeight: "30rem" }}>
              {/* Reports sidebar list */}
              <div style={{ borderRight: "1px solid var(--border)", paddingRight: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <button
                  type="button"
                  onClick={() => generateReport.mutate()}
                  disabled={generateReport.isPending}
                  className="shukan-btn shukan-btn-primary"
                  style={{ width: "100%", fontSize: "0.75rem" }}
                >
                  {generateReport.isPending ? "$ generating..." : "$ run agi evaluation loop"}
                </button>

                <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", borderBottom: "1px dashed var(--border)", paddingBottom: "0.25rem" }}>
                  archived evaluations
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", overflowY: "auto", maxHeight: "25rem" }}>
                  {isLoadingReports ? (
                    <span style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>loading reports...</span>
                  ) : reports && reports.length > 0 ? (
                    reports.map((r) => {
                      const isActive = selectedReportId === r.id;
                      return (
                        <div
                          key={r.id}
                          onClick={() => setSelectedReportId(r.id)}
                          style={{
                            border: `1px solid ${isActive ? "var(--accent)" : "var(--border)"}`,
                            padding: "0.4rem",
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            backgroundColor: isActive ? "var(--bg-elevated)" : "transparent",
                          }}
                        >
                          <div style={{ fontWeight: "bold", color: isActive ? "var(--accent)" : "var(--fg)" }}>
                            AGI-REPORT · {r.date}
                          </div>
                          <div style={{ fontSize: "0.65rem", color: "var(--fg-dim)", marginTop: "0.1rem" }}>
                            {new Date(r.createdAt).toLocaleTimeString()}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <span style={{ fontSize: "0.7rem", color: "var(--fg-dim)" }}>no reports generated yet.</span>
                  )}
                </div>
              </div>

              {/* Report display content */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                {generateReport.isPending && (
                  <div style={{ border: "1px solid var(--accent)", padding: "1rem", backgroundColor: "var(--bg-elevated)", fontFamily: "monospace" }}>
                    <div style={{ color: "var(--accent)" }} className="shukan-prompt-cursor">
                      $ executing AGI tools, fetching Google Calendar events...
                    </div>
                    <div style={{ color: "var(--fg-dim)", fontSize: "0.75rem", marginTop: "0.5rem" }}>
                      Analysing recent completions, streak values, journal states, and long-term memories. Writing diagnostic report...
                    </div>
                  </div>
                )}

                {currentSelectedReport ? (
                  <div
                    style={{
                      border: "1px solid var(--border)",
                      padding: "1.25rem",
                      backgroundColor: "var(--bg-elevated)",
                      overflowY: "auto",
                      maxHeight: "35rem",
                      fontSize: "0.85rem",
                      lineHeight: "1.6",
                      color: "var(--fg)",
                      whiteSpace: "pre-wrap",
                      fontFamily: "JetBrains Mono, monospace",
                    }}
                  >
                    <div style={{ fontSize: "0.7rem", color: "var(--accent)", borderBottom: "1px dashed var(--border)", paddingBottom: "0.5rem", marginBottom: "1rem" }}>
                      AGI EVALUATION REPORT ID: {currentSelectedReport.id} · GENERATED: {currentSelectedReport.date}
                    </div>
                    {currentSelectedReport.report}
                  </div>
                ) : (
                  !generateReport.isPending && (
                    <div style={{ border: "1px dashed var(--border)", padding: "2rem", textAlign: "center", color: "var(--fg-dim)", fontSize: "0.8rem" }}>
                      $ select an evaluation report from the sidebar or click "$ run agi evaluation loop" to perform an automated evaluation of your habits and routine.
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* TAB 3: AGENT MEMORY LOGS */}
          {activeTab === "memories" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>
                View and edit the long-term preferences, goals, and behavioral insights stored by your AGI Personal Assistant.
              </div>

              {/* Add memory form */}
              <form onSubmit={handleAddMemorySubmit} style={{ border: "1px solid var(--border)", padding: "1rem", backgroundColor: "var(--bg-elevated)", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                <div style={{ fontSize: "0.75rem", fontWeight: "bold", color: "var(--accent)" }}>
                  + inject new memory / goal
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <input
                    type="text"
                    placeholder="e.g. User prefers morning gym sessions, aiming to read 12 books this year."
                    value={newMemoryText}
                    onChange={(e) => setNewMemoryText(e.target.value)}
                    className="shukan-input"
                    style={{ flex: 1, minWidth: "250px" }}
                  />
                  <select
                    value={newMemoryType}
                    onChange={(e: any) => setNewMemoryType(e.target.value)}
                    className="shukan-input"
                    style={{ maxWidth: "120px" }}
                  >
                    <option value="preference">preference</option>
                    <option value="insight">insight</option>
                    <option value="goal">goal</option>
                  </select>
                  <button
                    type="submit"
                    className="shukan-btn shukan-btn-primary"
                    disabled={saveMemory.isPending}
                  >
                    {saveMemory.isPending ? "saving..." : "$ inject"}
                  </button>
                </div>
              </form>

              {/* Memory list */}
              <div style={{ border: "1px solid var(--border)", padding: "0.75rem", backgroundColor: "var(--bg-elevated)" }}>
                <div style={{ fontSize: "0.75rem", color: "var(--fg-dim)", borderBottom: "1px dashed var(--border)", paddingBottom: "0.25rem", marginBottom: "0.5rem" }}>
                  AGI memories database ({memories?.length ?? 0} vectors)
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxHeight: "25rem", overflowY: "auto" }}>
                  {isLoadingMemories ? (
                    <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>loading memory database...</span>
                  ) : memories && memories.length > 0 ? (
                    memories.map((m) => (
                      <div
                        key={m.id}
                        style={{
                          borderBottom: "1px solid var(--border)",
                          paddingBottom: "0.5rem",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "0.8rem",
                          gap: "1rem",
                        }}
                      >
                        <div>
                          <span
                            style={{
                              fontSize: "0.65rem",
                              backgroundColor: m.memory_type === "goal" ? "rgba(239, 68, 68, 0.15)" : m.memory_type === "preference" ? "rgba(59, 130, 246, 0.15)" : "rgba(16, 185, 129, 0.15)",
                              color: m.memory_type === "goal" ? "var(--danger)" : m.memory_type === "preference" ? "#60a5fa" : "#10b981",
                              padding: "0.1rem 0.3rem",
                              marginRight: "0.5rem",
                              borderRadius: "2px",
                              border: `1px solid ${m.memory_type === "goal" ? "var(--danger)" : m.memory_type === "preference" ? "#60a5fa" : "#10b981"}40`,
                            }}
                          >
                            {m.memory_type}
                          </span>
                          <span>{m.content}</span>
                          <span style={{ fontSize: "0.65rem", color: "var(--fg-dim)", marginLeft: "0.5rem" }}>
                            ({new Date(m.created_at).toLocaleDateString()})
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteMemory.mutate(m.id)}
                          disabled={deleteMemory.isPending}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--danger)",
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            padding: "0.1rem 0.3rem",
                          }}
                        >
                          delete
                        </button>
                      </div>
                    ))
                  ) : (
                    <span style={{ fontSize: "0.75rem", color: "var(--fg-dim)" }}>no memories recorded in the AGI database yet.</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
